import { useMemo, useState, type ReactNode } from 'react';
import { Checkbox, Field, MultiSearchSelect, SearchSelect, Select, TextArea, type SearchOption } from '@voice/ui';
import { SpeechField, type Speech } from './SpeechField';
import { useDirectory } from './common';

type Params = Record<string, unknown>;

interface Meta {
  label: string;
  hint?: string;
  /** Auswahlliste statt Eingabefeld: Wert → deutscher Text. */
  options?: [string, string][];
  /** Einheit hinter der Zahl (nur im Label). */
  unit?: string;
  /** Feld nur zeigen, wenn diese Bedingung erfüllt ist. */
  when?: (p: Params) => boolean;
  /** Mehrzeiliges Textfeld. */
  multiline?: boolean;
  /** Platzhalter-Beispiel. */
  placeholder?: string;
}

const yes = (p: Params, k: string, v: unknown) => p[k] === v;

/** Deutsche Beschriftungen je Baustein; `*.feld` gilt für alle Bausteine mit diesem Feld. */
const META: Record<string, Meta> = {
  // Gemeinsam
  'seconds': { label: 'Dauer', unit: 'Sekunden', hint: 'Wie lange gewartet wird.' },
  'timeoutSec': { label: 'Klingeldauer / Wartezeit', unit: 'Sekunden' },
  'retries': { label: 'Wiederholungen bei falscher Eingabe', hint: 'Wie oft nachgefragt wird, bevor es als „keine Antwort“ gilt.' },
  'variable': { label: 'Name der Variable', hint: 'Die Antwort wird so gespeichert, z. B. „kundennummer“ – später als {{kundennummer}} in Texten nutzbar.', placeholder: 'antwort' },

  // Ansage
  'play.audio': { label: 'Ansage' },
  'play.interruptible': { label: 'Anrufer darf die Ansage mit einer Taste überspringen' },

  // Menü
  'menu.prompt': { label: 'Ansage vor der Auswahl' },
  'menu.options': { label: 'Gültige Tasten', hint: 'Jede gewählte Taste bekommt unten einen eigenen Ausgang.' },
  'menu.timeoutSec': { label: 'Zeit zum Drücken', unit: 'Sekunden' },
  'menu.invalid': { label: 'Ansage bei ungültiger Taste', hint: 'Leer = „Diese Eingabe kenne ich nicht.“' },

  // Frage mit Eingabe
  'ask.prompt': { label: 'Frage' },
  'ask.kind': {
    label: 'Art der Antwort',
    options: [
      ['digits', 'Ziffernfolge (z. B. Kundennummer)'],
      ['phone', 'Telefonnummer'],
      ['yesno', 'Ja oder Nein'],
      ['choice', 'Auswahl aus Stichwörtern'],
      ['text', 'Freitext (gesprochen, wird abgeschrieben)'],
      ['recording', 'Sprachaufnahme (wird gespeichert)'],
    ],
  },
  'ask.input': {
    label: 'Eingabe per',
    options: [
      ['both', 'Tastatur oder Sprache'],
      ['dtmf', 'Nur Tastatur'],
      ['speech', 'Nur Sprache'],
    ],
    hint: 'Sprache braucht die Spracherkennung (STT) in der Server-Konfiguration.',
    when: (p) => !yes(p, 'kind', 'recording'),
  },
  'ask.minDigits': { label: 'Mindestens Ziffern', when: (p) => p['kind'] === 'digits' || p['kind'] === 'phone' },
  'ask.maxDigits': { label: 'Höchstens Ziffern', when: (p) => p['kind'] === 'digits' || p['kind'] === 'phone' },
  'ask.maxSeconds': { label: 'Längste Aufnahme', unit: 'Sekunden', when: (p) => yes(p, 'kind', 'recording') },
  'ask.confirm': { label: 'Antwort zur Bestätigung vorlesen („Ist das richtig?“)', when: (p) => !yes(p, 'kind', 'recording') && !yes(p, 'kind', 'yesno') },
  'ask.confirmText': { label: 'Text der Rückfrage', hint: '{{wert}} wird durch die verstandene Antwort ersetzt.', multiline: true, when: (p) => p['confirm'] === true && !yes(p, 'kind', 'recording') },
  'ask.skipKey': { label: 'Taste zum Überspringen (optional)', options: [['', 'Keine – die Frage kann nicht übersprungen werden'], ['*', 'Stern (*)'], ['#', 'Raute (#)'], ...['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map((k): [string, string] => [k, `Taste ${k}`])], hint: 'Drückt der Anrufer diese Taste, wird die Frage übersprungen. Dafür gibt es unten einen eigenen Ausgang „Übersprungen“.' },
  'ask.repeatText': { label: 'Text, wenn nichts verstanden wurde', multiline: true, when: (p) => !yes(p, 'kind', 'recording') },
  'ask.choices': { label: 'Auswahlmöglichkeiten', when: (p) => yes(p, 'kind', 'choice') },

  // Variable setzen / Bedingung
  'set.value': { label: 'Wert', hint: 'Text oder Platzhalter wie {{anrufer_nummer}}.' },
  'condition.left': { label: 'Prüfe', hint: 'Meist eine Variable, z. B. {{anrufer_firma}}.', placeholder: '{{variable}}' },
  'condition.op': {
    label: 'Bedingung',
    options: [
      ['eq', 'ist gleich'],
      ['ne', 'ist ungleich'],
      ['contains', 'enthält'],
      ['matches', 'passt auf Muster (regulärer Ausdruck)'],
      ['gt', 'ist größer als'],
      ['lt', 'ist kleiner als'],
      ['empty', 'ist leer'],
      ['notempty', 'ist nicht leer'],
    ],
  },
  'condition.right': { label: 'Vergleichswert', when: (p) => p['op'] !== 'empty' && p['op'] !== 'notempty' },

  // Weiterleiten
  'dial.extension': { label: 'Nebenstelle' },
  'dial.timeoutSec': { label: 'Klingeldauer', unit: 'Sekunden' },
  'ringgroup.extensions': { label: 'Nebenstellen (klingeln gleichzeitig)' },
  'ringgroup.timeoutSec': { label: 'Klingeldauer', unit: 'Sekunden' },
  'dialNumber.number': { label: 'Rufnummer', hint: 'Mit Vorwahl, z. B. +49 371 123456.' },
  'dialNumber.timeoutSec': { label: 'Klingeldauer', unit: 'Sekunden' },
  'voicemail.extension': { label: 'Anrufbeantworter von Nebenstelle' },
  'queuevoicemail.queueId': { label: 'Anrufbeantworter der Warteschlange' },
  'queue.queueId': { label: 'Warteschlange' },
  'flow.flowId': { label: 'Ablauf' },
  'schedule.scheduleId': { label: 'Geschäftszeiten' },

  // Nachricht / Webhook
  'message.subject': { label: 'Betreff der Nachricht' },
  'message.notifyEmail': { label: 'E-Mail-Benachrichtigung an (optional)', placeholder: 'info@firma.de' },
  'message.assignExtension': { label: 'Nachricht zuweisen an (optional)' },
  'webhook.url': { label: 'Adresse (URL)', placeholder: 'https://…' },
  'webhook.method': { label: 'Methode', options: [['POST', 'POST (Daten senden)'], ['GET', 'GET (nur abrufen)']] },
  'webhook.body': { label: 'Gesendete Daten (JSON)', multiline: true, when: (p) => p['method'] !== 'GET', hint: 'Platzhalter wie {{anrufer_nummer}} sind möglich.' },
  'webhook.timeoutSec': { label: 'Zeitlimit', unit: 'Sekunden' },

  // Auflegen
  'hangup.status': {
    label: 'Grund für den Anrufer',
    options: [
      ['480', 'Nicht erreichbar'],
      ['486', 'Besetzt'],
      ['404', 'Nummer nicht gefunden'],
      ['603', 'Abgelehnt'],
      ['503', 'Dienst nicht verfügbar'],
    ],
  },
};

/** Reihenfolge der Felder je Baustein (alles Übrige folgt dahinter). */
const ORDER: Record<string, string[]> = {
  ask: ['prompt', 'kind', 'input', 'variable', 'minDigits', 'maxDigits', 'maxSeconds', 'choices', 'retries', 'skipKey', 'confirm', 'confirmText', 'repeatText'],
  menu: ['prompt', 'options', 'timeoutSec', 'retries', 'invalid'],
  condition: ['left', 'op', 'right'],
};

const EXTRA: Record<string, string[]> = { ask: ['skipKey'] };

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

const metaOf = (type: string, key: string): Meta | undefined => META[`${type}.${key}`] ?? META[key];

export function ParamEditor({ type, params, onChange }: { type: string; params: Params; onChange: (p: Params) => void }) {
  const dir = useDirectory();
  const refs = useMemo<Record<string, SearchOption[]>>(() => {
    const d = dir.data;
    const ext = (d?.extensions ?? []).map((x) => ({ value: x.number, label: x.name, hint: x.number }));
    return {
      scheduleId: (d?.schedules ?? []).map((x) => ({ value: x.id, label: x.name })),
      flowId: (d?.flows ?? []).map((x) => ({ value: x.id, label: x.name, hint: x.number ?? '' })),
      queueId: (d?.queues ?? []).map((x) => ({ value: x.id, label: x.name, hint: x.number ?? '' })),
      extension: ext,
      assignExtension: ext,
      extensions: ext,
    };
  }, [dir.data]);

  const set = (k: string, v: unknown) => {
    const next = { ...params };
    if (v === undefined) delete next[k];
    else next[k] = v;
    onChange(next);
  };

  const order = ORDER[type] ?? [];
  // Neue optionale Felder erscheinen auch bei älteren, schon gespeicherten Bausteinen.
  const withExtras: Params = { ...Object.fromEntries((EXTRA[type] ?? []).filter((k) => !(k in params)).map((k) => [k, ''])), ...params };
  const keys = [...order.filter((k) => k in withExtras), ...Object.keys(withExtras).filter((k) => !order.includes(k))];
  if (keys.length === 0) return <p style={{ color: 'var(--text-muted)' }}>Dieser Baustein hat keine Einstellungen.</p>;

  const fields: ReactNode[] = [];
  for (const k of keys) {
    const v = withExtras[k];
    const m = metaOf(type, k);
    if (m?.when && !m.when(params)) continue;
    const label = (m?.label ?? k) + (m?.unit ? ` (${m.unit})` : '');
    const hint = m?.hint;

    if (m?.options) {
      fields.push(
        <Select key={k} label={label} hint={hint} value={String(v)} onChange={(e) => set(k, typeof v === 'number' ? Number(e.target.value) : e.target.value)}>
          {m.options.map(([val, text]) => (
            <option key={val} value={val}>
              {text}
            </option>
          ))}
        </Select>,
      );
    } else if (refs[k] && Array.isArray(v)) {
      fields.push(<MultiSearchSelect key={k} label={label} options={refs[k]!} values={v as string[]} onChange={(x) => set(k, x)} placeholder="Nebenstellen suchen …" />);
    } else if (refs[k] && typeof v === 'string') {
      fields.push(<SearchSelect key={k} label={label} options={refs[k]!} value={v} onChange={(x) => set(k, x)} placeholder="Suchen …" />);
    } else if (k === 'options' && Array.isArray(v)) {
      fields.push(<KeyPicker key={k} label={label} hint={hint} selected={v as string[]} onChange={(x) => set(k, x)} />);
    } else if (k === 'choices' && Array.isArray(v)) {
      fields.push(<ChoicesEditor key={k} label={label} choices={v as Choice[]} onChange={(x) => set(k, x)} />);
    } else if (typeof v === 'boolean') {
      fields.push(<Checkbox key={k} label={label} hint={hint} checked={v} onChange={(e) => set(k, e.target.checked)} />);
    } else if (typeof v === 'number') {
      fields.push(<Field key={k} label={label} hint={hint} type="number" value={String(v)} onChange={(e) => set(k, Number(e.target.value))} />);
    } else if (typeof v === 'string') {
      fields.push(
        m?.multiline || v.length > 60 || v.includes('\n') ? (
          <TextArea key={k} label={label} hint={hint} value={v} rows={3} onChange={(e) => set(k, e.target.value)} />
        ) : (
          <Field key={k} label={label} hint={hint} value={v} placeholder={m?.placeholder} onChange={(e) => set(k, e.target.value)} />
        ),
      );
    } else if (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).every((x) => x === 'audioId' || x === 'text')) {
      fields.push(<SpeechField key={k} label={label} hint={hint} value={v as Speech} onChange={(s) => set(k, s)} />);
    } else {
      fields.push(<JsonField key={k} label={label} value={v} onChange={(x) => set(k, x)} />);
    }
  }
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{fields}</div>;
}

// ---------------------------------------------------------------- Tasten des Menüs

function KeyPicker({ label, hint, selected, onChange }: { label: string; hint?: string | undefined; selected: string[]; onChange: (v: string[]) => void }) {
  const toggle = (k: string) => onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k].sort((a, b) => KEYS.indexOf(a) - KEYS.indexOf(b)));
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
      <legend style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8 }}>{label}</legend>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 8 }}>
        {KEYS.map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={selected.includes(k)}
            onClick={() => toggle(k)}
            style={{
              minHeight: 44,
              font: 'inherit',
              fontFamily: 'var(--font-mono)',
              color: selected.includes(k) ? 'var(--accent-foreground)' : 'var(--text-primary)',
              background: selected.includes(k) ? 'var(--accent)' : 'var(--bg-elevated)',
              border: `1px solid ${selected.includes(k) ? 'var(--accent)' : 'var(--border-primary)'}`,
              cursor: 'pointer',
            }}
          >
            {k}
          </button>
        ))}
      </div>
      {hint && <p style={{ margin: '8px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{hint}</p>}
    </fieldset>
  );
}

// ---------------------------------------------------------------- Auswahlmöglichkeiten einer Frage

interface Choice {
  value: string;
  digit?: string | undefined;
  keywords: string[];
}

function ChoicesEditor({ label, choices, onChange }: { label: string; choices: Choice[]; onChange: (c: Choice[]) => void }) {
  const update = (i: number, patch: Partial<Choice>) => onChange(choices.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <legend style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8 }}>{label}</legend>
      {choices.length === 0 && <p style={{ margin: 0, color: 'var(--text-muted)' }}>Noch keine. Jede Möglichkeit wird als gespeicherter Wert abgelegt.</p>}
      {choices.map((c, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 90px', gap: 12, padding: 12, border: '1px solid var(--border-subtle)' }}>
          <Field label="Gespeicherter Wert" value={c.value} onChange={(e) => update(i, { value: e.target.value })} placeholder="z. B. Rechnung" />
          <Field label="Taste" maxLength={1} value={c.digit ?? ''} onChange={(e) => update(i, { digit: e.target.value.replace(/[^0-9*#]/g, '') || undefined })} />
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Stichwörter (Komma getrennt)" value={c.keywords.join(', ')} onChange={(e) => update(i, { keywords: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} placeholder="rechnung, zahlung, beitrag" hint="Sagt der Anrufer eines davon, gilt diese Möglichkeit." />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <button type="button" onClick={() => onChange(choices.filter((_, j) => j !== i))} style={{ font: 'inherit', color: 'var(--status-busy)', background: 'transparent', border: '1px solid var(--border-primary)', minHeight: 36, padding: '0 12px', cursor: 'pointer' }}>
              Entfernen
            </button>
          </div>
        </div>
      ))}
      <div>
        <button type="button" onClick={() => onChange([...choices, { value: '', keywords: [] }])} style={{ font: 'inherit', color: 'var(--text-primary)', background: 'transparent', border: '1px solid var(--border-primary)', minHeight: 40, padding: '0 16px', cursor: 'pointer' }}>
          Möglichkeit hinzufügen
        </button>
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------- Notfall: unbekannte Felder als JSON

function JsonField({ label, value, onChange }: { label: string; value: unknown; onChange: (v: unknown) => void }) {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [bad, setBad] = useState(false);
  return (
    <TextArea
      label={`${label} (JSON)`}
      value={text}
      rows={Math.min(10, text.split('\n').length + 1)}
      hint={bad ? 'Kein gültiges JSON – die Änderung wird noch nicht übernommen.' : undefined}
      onChange={(e) => {
        setText(e.target.value);
        try {
          onChange(JSON.parse(e.target.value));
          setBad(false);
        } catch {
          setBad(true);
        }
      }}
      style={{ fontFamily: 'var(--font-mono)' }}
    />
  );
}
